import { createContext, useContext } from 'react'
import { useNavigation } from '@/app/hooks'
import { programRequirementsSheetConfig } from '@/app/components/ProgramRequirementsSheet/ProgramRequirementsSheet'
import { UtilityProgram } from '@/types/utilityProgram'
import { useBottomSheet } from '@/context/bottom-sheet-context'
import { useSurveyResponder } from '@/app/components/SurveyObserver'

type FlowStepConfig = {
  backDisabled: (step: number) => boolean
}

export type MultiProgramFlowTypes = 'devices' | 'onboarding'

export type MultiProgramFlowContext = {
  exitFlowOnBack: boolean
  onSaveForLater: () => void
  onProgramCardClicked: (program: UtilityProgram, enrolled?: boolean) => void
  onFlowComplete: () => void
  onExitFlow: () => void
  routePrefix: string
  flowStepConfig: FlowStepConfig
  flow: MultiProgramFlowTypes
}

export const MultiProgramDeviceFlowContext = createContext<
  MultiProgramFlowContext | undefined
>(undefined)

export const MultiProgramOnboardingFlowContext = createContext<
  MultiProgramFlowContext | undefined
>(undefined)

export const MultiProgramDeviceFlowProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const navigate = useNavigation()
  const { setBottomSheetContent, snapToHeader } = useBottomSheet()

  const flowStepConfig: FlowStepConfig = {
    backDisabled: (stepIndex) => stepIndex === 1,
  }

  const onExitFlow = () => {
    navigate.push('/devices')
  }

  const onSaveForLater = () => {
    navigate.push('/devices')
  }

  const onProgramCardClicked = (
    program: UtilityProgram,
    enrolled?: boolean,
  ) => {
    const path = `/utility-program/${program.id}/program-details`
    navigate.push(path)
    if (enrolled) {
      return
    }
    setBottomSheetContent(
      programRequirementsSheetConfig(
        {
          utilityProgram: program,
        },
        'devices',
      ),
      false,
    )
    snapToHeader()
  }

  const onFlowComplete = () => {
    navigate.push('/devices')
  }

  return (
    <MultiProgramDeviceFlowContext.Provider
      value={{
        exitFlowOnBack: false,
        onSaveForLater,
        onProgramCardClicked,
        onFlowComplete,
        onExitFlow,
        routePrefix: '',
        flow: 'devices',
        flowStepConfig,
      }}
    >
      {children}
    </MultiProgramDeviceFlowContext.Provider>
  )
}

export const MultiProgramOnboardingFlowProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const navigate = useNavigation()
  const { completeSurvey: completeOnboarding } =
    useSurveyResponder('Onboarding')
  const { setBottomSheetContent, snapToHeader } = useBottomSheet()

  const onSaveForLater = () => {
    navigate.push('/onboarding/program-finder')
  }

  const flowStepConfig: FlowStepConfig = {
    backDisabled: () => false,
  }

  const onExitFlow = () => {
    navigate.push('/onboarding/program-finder')
  }

  const onProgramCardClicked = (
    program: UtilityProgram,
    enrolled?: boolean,
  ) => {
    const path = `/onboarding/utility-program/${program.id}/program-details`
    navigate.push(path)
    if (enrolled) {
      return
    }
    setBottomSheetContent(
      programRequirementsSheetConfig(
        {
          utilityProgram: program,
        },
        'onboarding',
      ),
      false,
    )
    snapToHeader()
  }

  const onFlowComplete = () => {
    navigate.push('/devices')
    completeOnboarding()
  }

  return (
    <MultiProgramOnboardingFlowContext.Provider
      value={{
        exitFlowOnBack: false,
        onSaveForLater,
        onProgramCardClicked,
        onFlowComplete,
        onExitFlow,
        routePrefix: '/onboarding',
        flow: 'onboarding',
        flowStepConfig,
      }}
    >
      {children}
    </MultiProgramOnboardingFlowContext.Provider>
  )
}

export const useCurrentMultiProgramFlowContext = () => {
  const deviceFlow = useContext(MultiProgramDeviceFlowContext)
  const onboardingFlow = useContext(MultiProgramOnboardingFlowContext)

  if (!deviceFlow && !onboardingFlow) {
    throw new Error('A multi-program flow component is missing a flow provider')
  }

  return (deviceFlow || onboardingFlow) as MultiProgramFlowContext
}
